import axios from 'axios';
import { Quote, QuoteLineItem } from '../types/api';
import { QUOTES_API } from '../config';

const API_URL = QUOTES_API;

export interface GetQuotesOptions {
  skip?: number;
  take?: number;
  search?: string;
  status?: string;
  archived?: boolean;
}

/**
 * Get all quotes with pagination and filters
 */
export const getQuotes = async (
  options: GetQuotesOptions = {}
): Promise<{ data: Quote[]; totalRows: number }> => {
  const params: any = {
    skip: options.skip || 0,
    take: options.take || 100,
  };

  if (options.search) {
    params.search = options.search;
  }

  if (options.status) {
    params.status = options.status;
  }

  if (options.archived !== undefined) {
    params.archived = options.archived;
  }

  const [dataResponse, countResponse] = await Promise.all([
    axios.get(API_URL, { params }),
    axios.get(`${API_URL}count`, { params }),
  ]);

  return {
    data: dataResponse.data,
    totalRows: countResponse.data.rows,
  };
};

/**
 * Get a single quote by GUID
 */
export const getQuoteByGuid = async (guid: string): Promise<Quote> => {
  const response = await axios.get(`${API_URL}${guid}`);
  return response.data;
};

/**
 * Create a new quote
 */
export const createQuote = async (quote: Partial<Quote>): Promise<Quote> => {
  const response = await axios.post(API_URL, quote);
  return response.data;
};

/**
 * Update an existing quote
 */
export const updateQuote = async (
  guid: string,
  quote: Partial<Quote>
): Promise<Quote> => {
  const response = await axios.put(`${API_URL}${guid}`, quote);
  return response.data;
};

/**
 * Delete a quote (DRAFT only)
 */
export const deleteQuote = async (guid: string): Promise<void> => {
  await axios.delete(`${API_URL}${guid}`);
};

/**
 * Archive a quote
 */
export const archiveQuote = async (guid: string): Promise<Quote> => {
  const response = await axios.post(`${API_URL}${guid}/archive`);
  return response.data;
};

/**
 * Update quote status
 */
export const updateQuoteStatus = async (
  guid: string,
  status: string
): Promise<Quote> => {
  const response = await axios.post(`${API_URL}${guid}/status`, { status });
  return response.data;
};

/**
 * Retry failed Xero sync
 */
export const retrySyncQuote = async (guid: string): Promise<Quote> => {
  const response = await axios.post(`${API_URL}${guid}/retry-sync`);
  return response.data;
};

/**
 * Convert quote to Shopify order
 * (Will be implemented in Task 5)
 */
export const convertQuoteToOrder = async (
  guid: string
): Promise<{ orderId: string; orderNumber: string }> => {
  const response = await axios.post(`${API_URL}${guid}/convert-to-order`);
  return response.data;
};

/**
 * Search Xero customers
 * (Will be implemented in Task 4)
 */
export const searchXeroCustomers = async (query: string): Promise<any[]> => {
  const response = await axios.get(`${API_URL}search-customers`, {
    params: { query },
  });
  return response.data;
};

/**
 * Create a new customer in Xero
 * (Will be implemented in Task 4)
 */
export const createXeroCustomer = async (customer: {
  name: string;
  email?: string;
  phone?: string;
}): Promise<{ contactId: string }> => {
  const response = await axios.post(`${API_URL}create-customer`, customer);
  return response.data;
};
